import { liquidTone } from '../components/LiquidText';
import { OrcamentoIcon } from '../components/OrcamentoUI';
import { LiquidDialog, useLiquidFeedback } from '../components/LiquidUI';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import '../components/EquipmentDialogs.css';

/* ============================================================
   DOCUMENTAÇÃO DE EQUIPAMENTOS E FERRAMENTAS
   ISO 9001 — 7.1.5 / 7.5 · P_IM_01

   O local, dentro da aplicação, onde ficam guardadas em PDF as
   verificações e a restante documentação dos equipamentos — que foi
   o que a auditoria de 2025 pediu que fosse definido.

   Serve dois usos com o mesmo componente: passando `equipamentoId`
   mostra só os documentos desse equipamento (ficha do equipamento);
   sem ele, mostra a vista anual de tudo.
   ============================================================ */

const CAT_COR = {
    verificacao_dl50: { bg: '#dbeafe', fg: '#1e40af' },
    verificacao_andaime: { bg: '#e0e7ff', fg: '#3730a3' },
    registo_controlo: { bg: '#fae8ff', fg: '#86198f' },
    certificado: { bg: '#dcfce7', fg: '#166534' },
    declaracao_ce: { bg: '#ccfbf1', fg: '#115e59' },
    manual: { bg: '#f1f5f9', fg: '#475569' },
    outro: { bg: '#fef3c7', fg: '#92400e' },
};

// Só o manual e a declaração CE podem ficar à vista de quem lê o QR em obra
// e no separador Documentos do telemóvel (o servidor aplica a mesma regra).
const PODEM_SER_VISIVEIS = ['manual', 'declaracao_ce'];

const kb = (n) => {
    const v = parseInt(n, 10);
    if (!Number.isFinite(v)) return '—';
    return v >= 1048576 ? `${(v / 1048576).toFixed(1)} MB` : `${Math.round(v / 1024)} KB`;
};

/* ---------- Carregar documento ---------- */
function UploadModal({ ano, equipamentoId, categorias, equipamentos, onClose, onSaved }) {
    const [form, setForm] = useState({
        titulo: '',
        categoria: 'verificacao_dl50',
        ano: ano && ano !== 'todos' ? ano : new Date().getFullYear(),
        equipamento_id: equipamentoId || '',
        observacoes: '',
        visivel_colaboradores: true,
    });
    const [ficheiro, setFicheiro] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [err, setErr] = useState('');

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const guardar = async () => {
        if (!ficheiro) { setErr('Escolha um ficheiro.'); return; }
        setGuardando(true); setErr('');
        try {
            const fd = new FormData();
            fd.append('ficheiro', ficheiro);
            fd.append('titulo', form.titulo || ficheiro.name);
            fd.append('categoria', form.categoria);
            if (form.ano) fd.append('ano', form.ano);
            if (form.equipamento_id) fd.append('equipamento_id', form.equipamento_id);
            if (form.observacoes) fd.append('observacoes', form.observacoes);
            if (PODEM_SER_VISIVEIS.includes(form.categoria)) fd.append('visivel_colaboradores', form.visivel_colaboradores ? 'true' : 'false');
            await api.post('/certificacao-documentos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            onSaved(); onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao carregar o documento.');
        } finally { setGuardando(false); }
    };

    return (
        <LiquidDialog onClose={onClose} style={overlay} onClick={onClose}>
            <div className="equipment-dialog equipment-form-dialog" onClick={e => e.stopPropagation()}>
                <div className="equipment-dialog-header">
                    <div><h3><OrcamentoIcon name="upload" fallback="↑" /> Carregar documento</h3><p>Associe um PDF ao equipamento e à respetiva categoria.</p></div>
                    <button type="button" className="equipment-dialog-close" aria-label="Fechar carregamento" onClick={onClose}><OrcamentoIcon name="close" fallback="✕" /></button>
                </div>
                <div className="equipment-dialog-body">
                <div className="equipment-file-field" style={{ marginBottom: 18 }}>
                    <label data-liquid-label="true" style={lbl}>Ficheiro (PDF)</label>
                    <input type="file" accept=".pdf" onChange={e => setFicheiro(e.target.files[0] || null)}
                        style={{ ...inp, width: '100%', padding: 6 }} />
                </div>

                <div className="equipment-dialog-grid">
                    <div style={{ gridColumn: '1 / -1' }}>
                        <label data-liquid-label="true" style={lbl}>Título</label>
                        <input value={form.titulo} onChange={e => set('titulo', e.target.value)}
                            placeholder={ficheiro?.name || 'Nome do documento'} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Categoria</label>
                        <select value={form.categoria} onChange={e => set('categoria', e.target.value)} style={{ ...inp, width: '100%' }}>
                            {categorias.map(c => <option key={c.valor} value={c.valor}>{c.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Ano</label>
                        <input type="number" value={form.ano || ''} onChange={e => set('ano', e.target.value)}
                            placeholder="deixar vazio se não se aplica" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <label data-liquid-label="true" style={lbl}>Equipamento</label>
                        <select value={form.equipamento_id} onChange={e => set('equipamento_id', e.target.value)} style={{ ...inp, width: '100%' }}>
                            <option value="">— não associado a um equipamento —</option>
                            {equipamentos.map(e => <option key={e.id} value={e.id}>{e.nome}{e.n_serie ? ` (${e.n_serie})` : ''}</option>)}
                        </select>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <label data-liquid-label="true" style={lbl}>Observações</label>
                        <textarea value={form.observacoes} onChange={e => set('observacoes', e.target.value)}
                            rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                    </div>
                    {PODEM_SER_VISIVEIS.includes(form.categoria) && (
                        <label style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: '0.86rem' }}>
                            <input type="checkbox" checked={!!form.visivel_colaboradores}
                                onChange={e => set('visivel_colaboradores', e.target.checked)} style={{ marginTop: 3 }} />
                            <span>
                                <strong>Visível em obra</strong> — abre-se ao ler o QR da ferramenta e aparece no separador
                                Documentos do telemóvel. Use para o manual em português e a declaração CE.
                            </span>
                        </label>
                    )}
                </div>

                {err && <div style={{ color: '#e53e3e', marginTop: 10, fontSize: '0.85rem' }}>{err}</div>}

                </div>
                <div className="equipment-dialog-footer">
                    <button data-liquid-secondary="true" className="btn-secondary" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={guardando} style={btnPri}>
                        {guardando ? 'A carregar…' : 'Carregar'}
                    </button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Componente principal ---------- */
const DocumentosCertificacao = ({ equipamentoId = null, equipamentoNome = null }) => {
    const liquidFeedback = useLiquidFeedback();
    const soUmEquipamento = equipamentoId != null;
    const [ano, setAno] = useState('todos');
    const [categoria, setCategoria] = useState('todas');
    const [pesquisa, setPesquisa] = useState('');
    const [docs, setDocs] = useState([]);
    const [anos, setAnos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [equipamentos, setEquipamentos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [carregar_, setCarregar] = useState(false);

    useEffect(() => {
        api.get('/certificacao-documentos/categorias').then(r => setCategorias(r.data || [])).catch(() => { });
        api.get('/equipamentos').then(r => setEquipamentos(r.data || [])).catch(() => { });
    }, []);

    const carregarDocs = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (soUmEquipamento) params.equipamento_id = equipamentoId;
            const [rd, ra] = await Promise.all([
                api.get('/certificacao-documentos', { params }),
                api.get('/certificacao-documentos/anos'),
            ]);
            setDocs(rd.data || []);
            setAnos(Array.isArray(ra.data) ? ra.data : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [equipamentoId, soUmEquipamento]);
    useEffect(() => { carregarDocs(); }, [carregarDocs]);

    const abrir = async (d) => {
        try {
            const r = await api.get(`/certificacao-documentos/${d.id}/ficheiro`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
            window.open(url, '_blank');
        } catch { liquidFeedback.notify('Não foi possível abrir o documento.'); }
    };

    // Pôr ou tirar da vista em obra (QR da ferramenta e telemóvel)
    const alternarVisivel = async (d) => {
        try {
            await api.put(`/certificacao-documentos/${d.id}`, {
                ano: d.ano, categoria: d.categoria, equipamento_id: d.equipamento_id,
                titulo: d.titulo, observacoes: d.observacoes,
                visivel_colaboradores: !d.visivel_colaboradores,
            });
            carregarDocs();
        } catch { liquidFeedback.notify('Não foi possível alterar a visibilidade.'); }
    };

    const apagar = async (d) => {
        if (!await liquidFeedback.confirm(`Apagar "${d.titulo}"? O ficheiro sai do servidor quando nenhuma outra ficha o usar.`)) return;
        try { await api.delete(`/certificacao-documentos/${d.id}`); carregarDocs(); }
        catch { liquidFeedback.notify('Erro ao apagar.'); }
    };

    // Os filtros de ano e categoria aplicam-se em memória: a lista é pequena e
    // assim o seletor não obriga a ir ao servidor a cada mudança.
    let visiveis = docs;
    if (ano !== 'todos') {
        visiveis = ano === 'sem'
            ? visiveis.filter(d => d.ano == null)
            : visiveis.filter(d => String(d.ano) === String(ano));
    }
    if (categoria !== 'todas') visiveis = visiveis.filter(d => d.categoria === categoria);

    const termo = pesquisa.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    if (termo) visiveis = visiveis.filter(d => [d.titulo, d.nome_original, d.equipamento_nome, d.observacoes].filter(Boolean).join(' ').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(termo));

    const contagemAno = {};
    docs.forEach(d => { const k = d.ano ?? 'sem'; contagemAno[k] = (contagemAno[k] || 0) + 1; });

    return (
        <div className="cert-documents">
            <div className="cert-document-filters">
                <div className="cert-document-search">
                    <label data-liquid-label="true" style={lbl}>Pesquisar</label>
                    <input aria-label="Pesquisar documentos" value={pesquisa} onChange={e => setPesquisa(e.target.value)} placeholder="Documento, equipamento ou observações…" style={inp} />
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Ano</label>
                        <select aria-label="Filtrar documentos por ano" value={ano} onChange={e => setAno(e.target.value)} style={inp}>
                            <option value="todos">Todos ({docs.length})</option>
                            {anos.map(a => (
                                <option key={a} value={a}>{a} ({contagemAno[a] || 0})</option>
                            ))}
                            {contagemAno['sem'] > 0 && <option value="sem">Sem ano ({contagemAno['sem']})</option>}
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Categoria</label>
                        <select aria-label="Filtrar documentos por categoria" value={categoria} onChange={e => setCategoria(e.target.value)} style={inp}>
                            <option value="todas">Todas</option>
                            {categorias.map(c => <option key={c.valor} value={c.valor}>{c.label}</option>)}
                        </select>
                    </div>
                </div>
                <button className="cert-document-upload" data-liquid-primary="true" onClick={() => setCarregar(true)} style={btnPri}><OrcamentoIcon name="upload" fallback="↑" /> Carregar documento</button>
            </div>

            {soUmEquipamento && (
                <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '0.85rem' }}>
                    Documentação de <strong>{equipamentoNome}</strong>.
                </p>
            )}

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : visiveis.length === 0 ? (
                    <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>
                        Sem documentos para este filtro.
                    </div>
                ) : (
                    <div data-liquid-surface="true" className="table-responsive cert-documents-table" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                        <table className="standard-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'center', width: 70 }}>Ano</th>
                                    <th>Documento</th>
                                    <th>Categoria</th>
                                    {!soUmEquipamento && <th>Equipamento</th>}
                                    <th style={{ textAlign: 'center' }}>Tamanho</th>
                                    <th style={{ textAlign: 'center' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visiveis.map(d => {
                                    const cor = CAT_COR[d.categoria] || CAT_COR.outro;
                                    return (
                                        <tr key={d.id}>
                                            <td style={{ textAlign: 'center', fontWeight: 700 }}>{d.ano || '—'}</td>
                                            <td>
                                                {d.titulo || d.nome_original}
                                                {d.visivel_colaboradores && (
                                                    <span title="Abre-se ao ler o QR da ferramenta e aparece no telemóvel"
                                                        style={{ ...badge, marginLeft: 8, background: '#ecfdf5', color: '#047857', fontSize: '0.7rem' }}>
                                                        👷 em obra
                                                    </span>
                                                )}
                                                {d.observacoes && (
                                                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>{d.observacoes}</div>
                                                )}
                                            </td>
                                            <td>
                                                <span data-liquid-tone={liquidTone(cor.bg)} data-liquid-badge="true" style={{ ...badge, background: cor.bg, color: cor.fg }}>
                                                    {categorias.find(c => c.valor === d.categoria)?.label || ({ certificacao_apcer: 'Certificação APCER', consulta: 'Consulta', emergencia: 'Emergência', registo_sgi: 'Registo SGI' })[d.categoria] || d.categoria_label || d.categoria?.replace(/_/g, ' ')}
                                                </span>
                                            </td>
                                            {!soUmEquipamento && (
                                                <td>{d.equipamento_nome || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                                            )}
                                            <td style={{ textAlign: 'center', color: '#64748b', fontSize: '0.82rem' }}>{kb(d.tamanho)}</td>
                                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                <button data-liquid-secondary="true" aria-label={`Abrir PDF: ${d.titulo || d.nome_original}`} onClick={() => abrir(d)} style={{ ...btnSec, ...mini }} title="Abrir PDF"><OrcamentoIcon name="document" fallback="📄" /></button>
                                                {PODEM_SER_VISIVEIS.includes(d.categoria) && (
                                                    <button data-liquid-secondary="true" onClick={() => alternarVisivel(d)}
                                                        aria-pressed={!!d.visivel_colaboradores}
                                                        aria-label={`${d.visivel_colaboradores ? 'Tirar da' : 'Pôr na'} vista em obra: ${d.titulo || d.nome_original}`}
                                                        title={d.visivel_colaboradores ? 'Visível em obra — clique para deixar só no escritório' : 'Só no escritório — clique para mostrar em obra (QR e telemóvel)'}
                                                        style={{ ...btnSec, ...mini, ...(d.visivel_colaboradores ? { background: '#ecfdf5', borderColor: '#6ee7b7' } : { opacity: 0.55 }) }}>👷</button>
                                                )}
                                                <button data-liquid-secondary="true" aria-label={`Apagar documento: ${d.titulo || d.nome_original}`} onClick={() => apagar(d)} style={{ ...btnSec, ...mini, color: '#dc2626' }} title="Apagar"><OrcamentoIcon name="trash" fallback="🗑" /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

            <div className="cert-document-summary" role="status">{loading ? 'A carregar documentos…' : `${visiveis.length} de ${docs.length} documentos`}</div>
            {carregar_ && (
                <UploadModal ano={ano} equipamentoId={equipamentoId} categorias={categorias}
                    equipamentos={equipamentos} onClose={() => setCarregar(false)} onSaved={carregarDocs} />
            )}
        </div>
    );
};

const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '5px 10px', marginLeft: 6, fontSize: '0.78rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };

export default DocumentosCertificacao;
